/**
 * useGoalState — Root state hook composing all goal-related state and operations.
 */
import { Goal } from "@/types/todo";
import { useAuth } from "@/hooks/useAuth";
import { useGuestGoals } from "@/hooks/useGuestGoals";
import { useCloudGoals } from "@/hooks/useCloudGoals";
import { usePoints } from "@/hooks/usePoints";
import { useMilestoneAwarder } from "@/hooks/useMilestoneAwarder";
import { useCategorySelection } from "./useCategorySelection";
import { useGoalAddition } from "./useGoalAddition";
import { useGoalEventHandlers } from "./useGoalEventHandlers";
import { useGoalDeletionWithUndo } from "./useGoalDeletionWithUndo";
import { useNotificationState } from "@/hooks/useNotificationState";
import { formatDateForDatabase } from "@/hooks/goal-operations/helpers/dateUtils";
import { logger } from "@/utils/logger";

export const useGoalState = () => {
  const { user } = useAuth();
  const { points, addTaskPoints, addGoalPoints } = usePoints();
  const queueMilestone = useMilestoneAwarder();
  const { selectedCategory, setSelectedCategory } = useCategorySelection();
  const { hasAcknowledged } = useNotificationState();
  
  const guestGoals = useGuestGoals();
  const cloudGoals = useCloudGoals(user?.id);
  
  const {
    goals,
    addGoal,
    deleteGoal,
    updateGoal,
    addTodo,
    deleteTodo,
    toggleTodo,
    updateTodo,
    addSubtask,
    deleteSubtask,
    toggleSubtask,
    updateSubtask,
    addSubSubtask,
    deleteSubSubtask,
    toggleSubSubtask,
    updateTodoDueDate: rawUpdateTodoDueDate,
  } = user ? cloudGoals : guestGoals;

  const { hasAddedCustomGoal, handleExampleGoalClick } = useGoalAddition(addGoal);
  const { deleteGoalWithUndo } = useGoalDeletionWithUndo();

  const typedGoals: Goal[] = Array.isArray(goals) ? goals.map(goal => ({
    id: goal.id || crypto.randomUUID(),
    title: goal.title,
    progress: goal.progress,
    dueDate: goal.dueDate,
    category: goal.category,
    isExample: goal.isExample || false,
    completedAt: goal.completedAt,
    celebratedAt: goal.celebratedAt,
    userId: goal.userId,
    commonGoalId: goal.commonGoalId,
    todos: goal.todos.map(todo => ({
      id: todo.id,
      text: todo.text,
      completed: todo.completed,
      dueDate: todo.dueDate,
      subtasks: todo.subtasks
    }))
  })) : [];

  const operations = {
    addTodo: (params: { goalTitle: string; todoText: string; todoDueDate: string }) => {
      return Promise.resolve(addTodo(params));
    },
    deleteTodo: (params: { goalTitle: string; todoId: string }) => {
      return Promise.resolve(deleteTodo(params));
    },
    toggleTodo: (params: { goalTitle: string; todoId: string }) => {
      return Promise.resolve(toggleTodo(params));
    },
    updateTodo: (params: { goalTitle: string; todoId: string; newText: string }) => {
      return Promise.resolve(updateTodo(params));
    },
    addSubtask: (params: { goalTitle: string; todoId: string; subtaskText: string }) => {
      return Promise.resolve(addSubtask(params));
    },
    deleteSubtask: (params: { goalTitle: string; todoId: string; subtaskId: string }) => {
      return Promise.resolve(deleteSubtask(params));
    },
    toggleSubtask: (params: { goalTitle: string; todoId: string; subtaskId: string }) => {
      return Promise.resolve(toggleSubtask(params));
    },
    updateSubtask: (params: { goalTitle: string; todoId: string; subtaskId: string; newText: string }) => {
      return Promise.resolve(updateSubtask(params));
    },
    addSubSubtask: (params: { goalTitle: string; todoId: string; subtaskId: string; subSubtaskText: string }) => {
      return Promise.resolve(addSubSubtask(params));
    },
    deleteSubSubtask: (params: { goalTitle: string; todoId: string; subtaskId: string; subSubtaskId: string }) => {
      return Promise.resolve(deleteSubSubtask(params));
    },
    toggleSubSubtask: (params: { goalTitle: string; todoId: string; subtaskId: string; subSubtaskId: string }) => {
      return Promise.resolve(toggleSubSubtask(params));
    },
    updateGoal: (updatedGoal: Goal) => {
      const existingGoal = typedGoals.find(g => g.id === updatedGoal.id);
      const completedAtChanged = existingGoal?.completedAt !== updatedGoal.completedAt;
      const celebratedAtChanged = existingGoal?.celebratedAt !== updatedGoal.celebratedAt;
      const progressChanged = Math.round(existingGoal?.progress || 0) !== Math.round(updatedGoal.progress);
      const titleChanged = existingGoal?.title !== updatedGoal.title;
      const categoryChanged = existingGoal?.category !== updatedGoal.category;
      const dueDateChanged = existingGoal?.dueDate !== updatedGoal.dueDate;
      
      if (completedAtChanged || celebratedAtChanged || progressChanged || titleChanged || categoryChanged || dueDateChanged) {
        return Promise.resolve(updateGoal(updatedGoal));
      }
      
      return Promise.resolve();
    },
    updateTodoDueDate: (params: { goalTitle: string; todoId: string; newDate: Date | string | null }) => {
      try {
        return Promise.resolve(rawUpdateTodoDueDate(params));
      } catch (error) {
        logger.error('[GoalState] Error in updateTodoDueDate:', error);
        return Promise.reject(error);
      }
    }
  };

  const {
    handleAddTodo,
    handleDeleteTodo,
    handleToggleTodo,
    handleUpdateTodo,
    handleAddSubtask,
    handleDeleteSubtask,
    handleToggleSubtask,
    handleUpdateSubtask,
    handleAddSubSubtask,
    handleDeleteSubSubtask,
    handleToggleSubSubtask,
    handleUpdateDueDate
  } = useGoalEventHandlers(operations, typedGoals);

  const addNewGoalWithMilestone = async (goal: Goal) => {
    const result = addGoal(goal);
    if (user) {
      // Fire-and-forget; RPC is idempotent + only queues first time.
      queueMilestone("create_first_goal").catch(() => {});
    }
    return result;
  };

  return {
    goals: typedGoals,
    hasAddedCustomGoal,
    selectedCategory,
    points,
    setSelectedCategory,
    addNewGoal: addNewGoalWithMilestone,
    handleExampleGoalClick,
    handleDeleteGoal: user ? deleteGoalWithUndo : deleteGoal,
    handleUpdateGoal: operations.updateGoal,
    handleAddTodo,
    handleDeleteTodo,
    handleToggleTodo,
    handleUpdateTodo,
    handleAddSubtask,
    handleDeleteSubtask,
    handleToggleSubtask,
    handleUpdateSubtask,
    handleAddSubSubtask,
    handleDeleteSubSubtask,
    handleToggleSubSubtask,
    handleUpdateDueDate,
    hasAcknowledged
  };
};
